import {
  type DLQDeleteResponse,
  type DLQMessagesResponse,
  type DLQPurgeResponse,
  type DLQQueuesResponse,
  type DLQRedriveResponse,
  deleteMessage,
  getDLQMessages,
  getDLQQueues,
  purgeQueue,
  redriveMessages,
} from '@/services/dlq.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// Query keys
export const dlqKeys = {
  all: ['dlq'] as const,
  queues: () => [...dlqKeys.all, 'queues'] as const,
  messages: (queueName: string) => [...dlqKeys.all, 'messages', queueName] as const,
}

// Hooks
export function useDLQQueues() {
  return useQuery<DLQQueuesResponse>({
    queryKey: dlqKeys.queues(),
    queryFn: () => getDLQQueues(),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

export function useDLQMessages(queueName: string, enabled = true) {
  return useQuery<DLQMessagesResponse>({
    queryKey: dlqKeys.messages(queueName),
    queryFn: () => getDLQMessages(queueName),
    enabled: enabled && !!queueName,
    refetchInterval: 15000, // Refresh every 15 seconds
  })
}

export function useRedriveMessages() {
  const queryClient = useQueryClient()

  return useMutation<DLQRedriveResponse, Error, { queueName: string; messageIds?: string[] }>({
    mutationFn: ({ queueName, messageIds }) => redriveMessages(queueName, messageIds),
    onSuccess: (_, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: dlqKeys.queues() })
      queryClient.invalidateQueries({ queryKey: dlqKeys.messages(queueName) })
    },
  })
}

export function useDeleteMessage() {
  const queryClient = useQueryClient()

  return useMutation<DLQDeleteResponse, Error, { queueName: string; receiptHandle: string }>({
    mutationFn: ({ queueName, receiptHandle }) => deleteMessage(queueName, receiptHandle),
    onSuccess: (_, { queueName }) => {
      queryClient.invalidateQueries({ queryKey: dlqKeys.queues() })
      queryClient.invalidateQueries({ queryKey: dlqKeys.messages(queueName) })
    },
  })
}

export function usePurgeQueue() {
  const queryClient = useQueryClient()

  return useMutation<DLQPurgeResponse, Error, string>({
    mutationFn: (queueName) => purgeQueue(queueName),
    onSuccess: (_, queueName) => {
      queryClient.invalidateQueries({ queryKey: dlqKeys.queues() })
      queryClient.invalidateQueries({ queryKey: dlqKeys.messages(queueName) })
    },
  })
}
